import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  dealsAPI,
  stagesAPI,
  companiesAPI,
  contactsAPI,
  appointmentsAPI,
} from "../services/api";
import { getUserInfo } from "../utils/helpers";

export const useLeadData = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlPipeline = queryParams.get("pipeline") || "meta";

  const [pipeline, setPipeline] = useState(urlPipeline);

  useEffect(() => {
    setPipeline(urlPipeline);
  }, [urlPipeline]);

  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({}); // New filters state

  const { userId, userName } = getUserInfo();
  const ownerName = userName || localStorage.getItem("companyName") || localStorage.getItem("emailNo") || "";

  console.log(deals, "deals");

  // Fetch Stages
  useEffect(() => {
    const fetchStages = async () => {
      try {
        const res = await stagesAPI.getAll(userId, { pipeline });
        setStages(res.data);
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };
    if (userId) fetchStages();
  }, [userId, pipeline]);

  // Fetch Companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await companiesAPI.getAll(userId);
        setCompanies(res.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    if (userId) fetchCompanies();
  }, [userId]);

  // Fetch Contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await contactsAPI.getAll(userId);
        setContacts(res.data);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    };
    if (userId) fetchContacts();
  }, [userId]);

  // Fetch Appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const res = await appointmentsAPI.getAll({ userId });
        setAppointments(res.validatedAppointments || res.data.appointments || []);
      } catch (error) {
        console.error("Error fetching appointments:", error);
      }
    };
    if (userId) fetchAppointments();
  }, [userId]);

  // Fetch Deals
  useEffect(() => {
    if (userId) fetchDeals(filters);
  }, [userId, userName, filters, pipeline]); // Re-fetch when filters or pipeline change

  const fetchDeals = async (currentFilters = {}) => {
    try {
      const role = localStorage.getItem("userRole");
      const memberId = localStorage.getItem("memberId");

      const res = await dealsAPI.getAll(userId, { role, memberId, pipeline, ...currentFilters });

      const formatted = res.data.map((d) => ({
        id: d._id,
        dealName: d.dealName,
        company: d.company?.name || "—",
        contactName: d.contact
          ? `${d.contact.firstName} ${d.contact.lastName}`
          : "—",
        stage: d.stage?._id || d.stage || "",
        stageName: d.stage?.name || "",
        amount: d.amount || 0,
        closingDate: d.closingDate,
        owner: ownerName,
        priority: d.priority?.toLowerCase() || "medium",
        description: d.description || "",
        source: d.source || "",
        mobile: d.mobile,
        createdAt: d.createdAt,
        email: d.email,
        interest: d.interest || "",
        subInterest: d.subInterest || "",
        disposition: d.disposition || "",
        subDisposition: d.subDisposition || "",
        followUpDate: d.followUpDate || null,
        assigneeName: d.assignee?.name || (d.assignee?.firstName ? `${d.assignee.firstName} ${d.assignee.lastName || ""}`.trim() : (typeof d.assignee === "string" ? d.assignee : "—")),
        // POC Fields
        pocIdentified: d.pocIdentified || "",
        pocName: d.pocName || "",
        pocPhone: d.pocPhone || "",
        pocEmail: d.pocEmail || "",
        pocComments: d.pocComments || "",
        // Complaint / Ticket Fields
        complaintDate: d.complaintDate || "",
        complaintTime: d.complaintTime || "",
        loggedBy: d.loggedBy || "",
        callerName: d.callerName || "",
        paymentType: d.paymentType || "",
        machineNumber: d.machineNumber || "",
        reasonForContact: d.reasonForContact || "",
        complaintType: d.complaintType || "",
        errorCode: d.errorCode || "",
        juicesPushed: d.juicesPushed || "",
        creditNoteJuices: d.creditNoteJuices || "",
        commandCode: d.commandCode || "",
        complaintStatus: d.complaintStatus || "",
        refundAmount: d.refundAmount || "",
        customerEmailAddress: d.customerEmailAddress || "",
        complaintComments: d.complaintComments || "",
        city: d.city || "",
        locationArea: d.locationArea || "",
        notes: d.notes || [],
      }));

      setDeals(formatted);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching deals:", error);
      setLoading(false);
    }
  };

  // Deal Operations
  const dealOperations = {
    create: async (payload) => {
      try {
        const res = await dealsAPI.create({ ...payload, pipeline, userId });
        const d = res.data.deal;
        const formatted = {
          id: d._id,
          dealName: d.dealName,
          company: d.company?.name || "—",
          contactName: d.contact
            ? `${d.contact.firstName} ${d.contact.lastName}`
            : "—",
          stage: d.stage?._id || d.stage || "",
          stageName: d.stage?.name || "",
          amount: d.amount || 0,
          closingDate: d.closingDate || "",
          owner: ownerName || "",
          priority: d.priority?.toLowerCase() || "medium",
          description: d.description || "",
          source: d.source || "",
          city: d.city || "",
          locationArea: d.locationArea || "",
          interest: d.interest || "",
          subInterest: d.subInterest || "",
          // Complaint Fields
          complaintDate: d.complaintDate || "",
          complaintTime: d.complaintTime || "",
          loggedBy: d.loggedBy || "",
          callerName: d.callerName || "",
          paymentType: d.paymentType || "",
          machineNumber: d.machineNumber || "",
          reasonForContact: d.reasonForContact || "",
          complaintType: d.complaintType || "",
          errorCode: d.errorCode || "",
          juicesPushed: d.juicesPushed || "",
          creditNoteJuices: d.creditNoteJuices || "",
          commandCode: d.commandCode || "",
          complaintStatus: d.complaintStatus || "",
          refundAmount: d.refundAmount || "",
          customerEmailAddress: d.customerEmailAddress || "",
          complaintComments: d.complaintComments || "",
        };
        setDeals((prev) => [...prev, formatted]);
        await fetchDeals();
        return { success: true };
      } catch (error) {
        console.error("Error creating deal:", error);
        return { success: false, error };
      }
    },

    update: async (dealId, payload) => {
      console.log(payload, "payload");
      try {
        const res = await dealsAPI.update(dealId, payload);
        const updated = res.data.deal;

        const formatted = {
          id: updated._id,
          _id: updated._id,
          dealName: updated.dealName,
          company: updated.company || null,
          contact: updated.contact || null,
          stage: updated.stage?._id || updated?.stage || "",
          amount: updated.amount || 0,
          closingDate: updated.closingDate,
          priority: updated.priority?.toLowerCase() || "medium",
          description: updated.description || "",
          source: updated.source || "",
          mobile: updated.mobile || "",
          // POC Fields
          pocIdentified: updated.pocIdentified || "",
          pocName: updated.pocName || "",
          pocPhone: updated.pocPhone || "",
          pocEmail: updated.pocEmail || "",
          pocComments: updated.pocComments || "",
          // Complaint / Ticket Fields
          complaintDate: updated.complaintDate || "",
          complaintTime: updated.complaintTime || "",
          loggedBy: updated.loggedBy || "",
          callerName: updated.callerName || "",
          paymentType: updated.paymentType || "",
          machineNumber: updated.machineNumber || "",
          reasonForContact: updated.reasonForContact || "",
          complaintType: updated.complaintType || "",
          errorCode: updated.errorCode || "",
          juicesPushed: updated.juicesPushed || "",
          creditNoteJuices: updated.creditNoteJuices || "",
          commandCode: updated.commandCode || "",
          complaintStatus: updated.complaintStatus || "",
          refundAmount: updated.refundAmount || "",
          customerEmailAddress: updated.customerEmailAddress || "",
          complaintComments: updated.complaintComments || "",
          city: updated.city || "",
          locationArea: updated.locationArea || "",
          interest: updated.interest || "",
          subInterest: updated.subInterest || "",
        };
        console.log(formatted, "formatted");
        setDeals((prev) => prev.map((d) => (d.id === dealId ? formatted : d)));
        return { success: true, deal: formatted };
      } catch (error) {
        console.error("Error updating deal:", error);
        return { success: false, error };
      }
    },

    delete: async (dealId) => {
      try {
        await dealsAPI.delete(dealId);
        setDeals((prev) => prev.filter((d) => d.id !== dealId));
        return { success: true };
      } catch (error) {
        console.error("Error deleting deal:", error);
        return { success: false, error };
      }
    },

    move: async (dealId, targetStageId) => {
      const deal = deals.find((d) => d.id === dealId);
      if (!deal) return { success: false };

      const previousStage = deal.stage;

      // Optimistic update
      setDeals((prev) =>
        prev.map((d) => (d.id === dealId ? { ...d, stage: targetStageId } : d))
      );

      try {
        const targetStage = stages.find(
          (s) => s._id === targetStageId || s.id === targetStageId
        );
        if (!targetStage) return { success: false };

        const res = await dealsAPI.update(dealId, { stage: targetStage._id });

        if (res.status === 200) {
          return { success: true };
        } else {
          throw new Error("Unexpected response");
        }
      } catch (error) {
        console.error("Error moving deal:", error);
        // Rollback
        setDeals((prev) =>
          prev.map((d) =>
            d.id === dealId ? { ...d, stage: previousStage } : d
          )
        );
        return { success: false, error };
      }
    },
  };

  // Stage Operations
  const stageOperations = {
    create: async (values) => {
      try {
        const res = await stagesAPI.create({ ...values, pipeline, userId });
        setStages((prev) => {
          const updated = [...prev, res.data.stage];
          return updated.sort((a, b) => (a.order || 0) - (b.order || 0));
        });
        return { success: true };
      } catch (error) {
        console.error("Error creating stage:", error);
        return { success: false, error };
      }
    },

    update: async (stageId, values) => {
      try {
        const res = await stagesAPI.update(stageId, values);
        setStages((prev) => {
          const updated = prev.map((s) => (s._id === stageId ? res.data.stage : s));
          return updated.sort((a, b) => (a.order || 0) - (b.order || 0));
        });
        return { success: true };
      } catch (error) {
        console.error("Error updating stage:", error);
        return { success: false, error };
      }
    },

    delete: async (stageId) => {
      try {
        const res = await stagesAPI.delete(stageId);
        setStages((prev) => prev.filter((s) => s._id !== stageId));
        return { success: true, message: res.data.message };
      } catch (error) {
        console.error("Error deleting stage:", error);
        return { success: false, error };
      }
    },
  };

  // Company Operations
  const companyOperations = {
    create: async (values) => {
      try {
        const res = await companiesAPI.create({ ...values, userId });
        setCompanies((prev) => [...prev, res.data.company]);
        return { success: true };
      } catch (error) {
        console.error("Error creating company:", error);
        return { success: false, error };
      }
    },
  };

  // Contact Operations
  const contactOperations = {
    create: async (payload) => {
      try {
        const res = await contactsAPI.create({ ...payload, userId });
        setContacts((prev) => [...prev, res.data.contact]);
        return { success: true };
      } catch (error) {
        console.error("Error creating contact:", error);
        return { success: false, error };
      }
    },
  };

  // Notes Operations
  const noteOperations = {
    add: (dealId, noteText) => {
      if (!noteText.trim()) return;
      const newNote = {
        id: String(Date.now()),
        dealId,
        text: noteText,
        author: userName,
        createdAt: new Date().toISOString(),
      };
      setNotes([...notes, newNote]);
      return { success: true };
    },

    delete: (noteId) => {
      setNotes(notes.filter((n) => n.id !== noteId));
      return { success: true };
    },

    getByDeal: (dealId) => {
      return notes
        .filter((n) => n.dealId === dealId)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
  };

  return {
    deals,
    stages,
    companies,
    contacts,
    appointments,
    notes,
    loading,
    filters,
    setFilters,
    fetchDeals,
    dealOperations,
    stageOperations,
    companyOperations,
    contactOperations,
    noteOperations,
    pipeline,
    setPipeline,
  };
};
